import { useState, useEffect } from "react";
import type { CouponType } from "../../../../src/types";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Pencil, Plus, Trash, X } from "lucide-react";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import { Spinner } from "@knocklabs/react";
import { CouponForm } from "./CouponForm";
import { useAuth } from "../../lib/authContext";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from "../ui/table";
export const CouponsList = () => {
  const [coupons, setCoupons] = useState<CouponType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [couponToEdit, setCouponToEdit] = useState<CouponType | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const fetchCoupons = async () => {
      const response = await api.getCoupons();
      setCoupons(response.results);
    };

    fetchCoupons();
  }, []);

  const convertDiscountType = (str: string) => {
    return str
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const updateCouponStatus = async (id: number, isActive: boolean) => {
    setIsLoading(true);

    const response = await api.updateCouponStatus(
      id,
      isActive,
      user?.id || "system",
    );
    if (response.success) {
      toast({
        title: `Coupon ${response.results.code} is ${
          isActive ? "activated" : "deactivated"
        }`,
      });
    }
    setIsLoading(false);
    setCoupons(
      coupons.map(coupon =>
        coupon.id === id ? { ...coupon, is_active: isActive } : coupon,
      ),
    );
  };

  const updateCouponsList = async () => {
    setIsLoading(true);
    const response = await api.getCoupons();
    setCoupons(response.results);
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col items-center">
      <div className="flex w-full justify-between items-center">
        <h2 className="text-2xl font-bold">Coupons</h2>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus size={20} className="mr-2" />
          Add Coupon
        </Button>
      </div>
      <div className="flex flex-col mt-4 w-full items-center">
        {isLoading ? (
          <div className="text-center text-gray-500">
            <Spinner />
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center text-gray-500">No coupons found</div>
        ) : (
          <Table className="bg-white dark:bg-gray-900 dark:text-white rounded-lg shadow-sm">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Min Cart Value</TableHead>
                <TableHead>Discount Type</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons.map(coupon => (
                <TableRow key={coupon.id} className="border-b">
                  <TableCell>{coupon.code}</TableCell>
                  <TableCell>{coupon.min_cart_value || "N/A"}</TableCell>
                  <TableCell>
                    {convertDiscountType(coupon.discount_type)}
                  </TableCell>
                  <TableCell>{coupon.discount_value}</TableCell>
                  <TableCell>
                    {coupon.expires_at
                      ? new Date(coupon.expires_at).toLocaleString()
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={coupon.is_active}
                      onCheckedChange={() => {
                        if (coupon.id) {
                          updateCouponStatus(coupon.id, !coupon.is_active);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      className="mr-2"
                      onClick={() => {
                        setIsModalOpen(true);
                        setCouponToEdit(coupon);
                      }}
                    >
                      <Pencil className="mr-2" size={20} />
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 dark:bg-gray-900 dark:text-white w-1/2 rounded-lg shadow-lg">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold mb-4">
                {couponToEdit ? "Edit Coupon" : "Add Coupon"}
              </h2>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  setCouponToEdit(null);
                }}
              >
                <X size={20} />
              </Button>
            </div>
            <CouponForm
              couponToEdit={couponToEdit}
              setCouponToEdit={setCouponToEdit}
              onSuccess={() => {
                setIsModalOpen(false);
                setCouponToEdit(null);
                updateCouponsList();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
